import { AIMessage } from "../data/types";

import { isWithinTokenLimit } from "gpt-tokenizer/model/gpt-3.5-turbo";
import { ConversationChunk } from "../data/types";
import {
  summarizeAIChatSummary,
  summarizeCurrentAIChat,
} from "../data/gemini-summarize";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";
import * as Sentry from "@sentry/react";
import { db } from "../data/firebase";

export const runSummaries = async (
  lastChunk: ConversationChunk,
  userId: string,
  conversationId: string
) => {
  if (!lastChunk.aiChat || lastChunk.aiChat.messages.length === 0) {
    return lastChunk;
  }

  // Find the index of the last summary message if one exists
  let summaryIndex = null;
  for (let i = lastChunk.aiChat.messages.length - 1; i >= 0; i--) {
    if (lastChunk.aiChat.messages[i].role === "summary") {
      summaryIndex = i;
      break;
    }
  }

  try {
    const [{ summaryOfNewMessages, newIndexForSummary }, newSummaryContent] =
      await Promise.all([
        checkAndSummarizeAIChat(
          lastChunk,
          summaryIndex,
          userId,
          conversationId
        ),
        checkAndSummarizeAIChatSummary(
          lastChunk,
          summaryIndex,
          userId,
          conversationId
        ),
      ]);

    // Neither function triggered, so we don't need to update the chunk
    if (summaryOfNewMessages.length === 0 && newSummaryContent.length === 0) {
      return lastChunk;
    }

    const summaryMessage: AIMessage = {
      id: crypto.randomUUID(),
      role: "summary",
      type: "text",
      content: [],
      isSummary: true,
    };

    if (newSummaryContent.length > 0) {
      // If the new summary content is not empty, use it
      summaryMessage.content = newSummaryContent;
    } else if (summaryIndex && typeof summaryIndex === "number") {
      // If the old summary content is not empty, use it
      summaryMessage.content = lastChunk.aiChat.messages[summaryIndex].content;
    }

    if (summaryOfNewMessages.length > 0) {
      summaryMessage.content = [
        ...summaryMessage.content,
        ...summaryOfNewMessages,
      ];
    }

    if (newIndexForSummary) {
      // Filter out the previous summary message
      const filteredMessages = lastChunk.aiChat.messages.filter(
        (msg, idx) => idx !== summaryIndex || summaryIndex === 0
      );

      lastChunk = {
        ...lastChunk,
        aiChat: {
          messages: [
            ...filteredMessages.slice(0, newIndexForSummary),
            summaryMessage,
            ...filteredMessages.slice(newIndexForSummary),
          ],
        },
      };
    } else if (summaryIndex) {
      lastChunk.aiChat.messages[summaryIndex] = summaryMessage;
    }

    return lastChunk;
  } catch (error) {
    console.error("Failed to generate summaries:", error);
    Sentry.captureException(error, {
      tags: {
        userId,
        conversationId,
        chunkId: lastChunk.id,
        component: "runSummaries",
        operation: "parallel-summarization",
      },
    });
    return lastChunk;
  }
};

const checkAndSummarizeAIChat = async (
  lastChunk: ConversationChunk,
  summaryIndex: number | null,
  userId?: string,
  conversationId?: string
) => {
  console.log(
    "checkAndSummarizeAIChat: summaryIndex =",
    summaryIndex,
    "lastChunk.aiChat exists:",
    !!lastChunk.aiChat
  );
  try {
    if (!lastChunk.aiChat || lastChunk.aiChat.messages.length === 0) {
      console.log(
        "checkAndSummarizeAIChat: No aiChat or no messages, returning early."
      );
      return { summaryOfNewMessages: [], newIndexForSummary: null };
    }

    // Determine the actual messages to consider for this summarization pass
    // These are messages that came *after* the previous summary, or all messages if no previous summary.
    const newMessagesSinceLastSummary =
      summaryIndex === null
        ? lastChunk.aiChat.messages
        : lastChunk.aiChat.messages.slice(summaryIndex + 1);

    if (newMessagesSinceLastSummary.length === 0) {
      console.log(
        "checkAndSummarizeAIChat: No new messages since last summary, returning early."
      );
      return { summaryOfNewMessages: [], newIndexForSummary: null };
    }

    // Map to {role, content} for consistent token counting with original logic
    const newMessagesMappedForTokenCheck = newMessagesSinceLastSummary.map(
      (m) => ({
        role: m.role,
        content: m.content,
      })
    );

    const isOverTokenLimitForNew = !isWithinTokenLimit(
      JSON.stringify(newMessagesMappedForTokenCheck),
      5000 // will be 5000
    );

    if (isOverTokenLimitForNew) {
      // If new messages since last summary > 5000 tokens
      console.log(
        "checkAndSummarizeAIChat: New messages > 5000 tokens. Proceeding to summarize a portion."
      );

      let cutoffToSummarizeCount = 0;
      const tailBuffer = [];
      for (let i = newMessagesMappedForTokenCheck.length - 1; i >= 0; i--) {
        tailBuffer.unshift(newMessagesMappedForTokenCheck[i]);
        if (!isWithinTokenLimit(JSON.stringify(tailBuffer), 3000)) {
          // will be 3000
          cutoffToSummarizeCount = i + 1;
          break;
        }
      }

      if (
        cutoffToSummarizeCount === 0 &&
        newMessagesSinceLastSummary.length > 0
      ) {
        Sentry.captureMessage(
          "checkAndSummarizeAIChat: cutoffToSummarizeCount is 0 unexpectedly.",
          {
            level: "warning",
            extra: {
              summaryIndex,
              numNewMessages: newMessagesSinceLastSummary.length,
              isOverTokenLimitForNew,
              userId,
              conversationId,
              chunkId: lastChunk.id,
            },
          }
        );
        cutoffToSummarizeCount = newMessagesSinceLastSummary.length;
      }

      if (cutoffToSummarizeCount === 0) {
        console.log(
          "checkAndSummarizeAIChat: cutoffToSummarizeCount is 0, no messages to summarize for this path."
        );
        return { summaryOfNewMessages: [], newIndexForSummary: null };
      }

      const messagesToActuallySummarize = newMessagesSinceLastSummary.slice(
        0,
        cutoffToSummarizeCount
      );

      console.log(
        `checkAndSummarizeAIChat: Summarizing ${messagesToActuallySummarize.length} messages.`
      );

      const { summary, tokensBefore, tokensAfter } =
        await summarizeCurrentAIChat({
          aiChat: messagesToActuallySummarize, // Pass full AIMessage objects
          pastSummary:
            summaryIndex !== null && lastChunk.aiChat?.messages[summaryIndex]
              ? lastChunk.aiChat.messages[summaryIndex]
              : null,
        });

      if (userId && conversationId) {
        addStatToAdmin(
          tokensBefore ?? 0,
          tokensAfter ?? 0,
          userId,
          conversationId,
          "messages",
          messagesToActuallySummarize.length, // Use length of messages actually summarized
          undefined,
          undefined
        ).catch((error) => {
          console.error("Failed to add stats to admin:", error);
        });
      }

      Sentry.addBreadcrumb({
        category: "summarization",
        message: "Successfully summarized AI chat messages",
        level: "info",
        data: {
          userId,
          conversationId,
          chunkId: lastChunk.id,
          tokensBefore,
          tokensAfter,
          messagesCount: messagesToActuallySummarize.length, // Use length of messages actually summarized
        },
      });

      Sentry.captureMessage("AI Chat Messages Summarized", {
        level: "info",
        tags: {
          userId,
          conversationId,
          chunkId: lastChunk.id,
          component: "checkAndSummarizeAIChat",
          tokensBefore: tokensBefore ?? 0,
          tokensAfter: tokensAfter ?? 0,
          compressionRatio:
            (tokensBefore ?? 0) > 0 && (tokensAfter ?? 0) > 0
              ? ((tokensBefore ?? 0) / (tokensAfter ?? 0)).toFixed(2)
              : "N/A",
        },
      });

      const baseIndexOfNewMessages =
        summaryIndex === null ? 0 : summaryIndex + 1;
      const finalNewIndexForSummary =
        baseIndexOfNewMessages + cutoffToSummarizeCount;

      console.log(
        `checkAndSummarizeAIChat: Generated summary. newIndexForSummary = ${finalNewIndexForSummary}`
      );

      return {
        summaryOfNewMessages: summary,
        newIndexForSummary: finalNewIndexForSummary,
      };
    } else {
      console.log(
        "checkAndSummarizeAIChat: New messages are <= 5000 tokens. No summarization needed by this function."
      );
      return { summaryOfNewMessages: [], newIndexForSummary: null };
    }
  } catch (error) {
    console.error("Failed to summarize AI chat:", error);
    Sentry.captureException(error, {
      tags: {
        userId,
        conversationId,
        chunkId: lastChunk.id,
        component: "checkAndSummarizeAIChat",
        operation: "summarize-messages",
      },
    });
    return { summaryOfNewMessages: [], newIndexForSummary: null };
  }
};

const checkAndSummarizeAIChatSummary = async (
  lastChunk: ConversationChunk,
  summaryIndex: number | null,
  userId?: string,
  conversationId?: string
): Promise<string[]> => {
  try {
    if (
      summaryIndex === null ||
      !lastChunk.aiChat ||
      lastChunk.aiChat.messages[summaryIndex].content.length === 0
    ) {
      return [];
    }

    const aiChatSummary = lastChunk.aiChat.messages[summaryIndex]
      .content as string[];

    const isWithinLimit = isWithinTokenLimit(
      JSON.stringify(aiChatSummary),
      7000
    );
    if (isWithinLimit) return [];

    let cutoffIndex = 0;
    const recentMessages = [];

    for (let i = aiChatSummary.length - 1; i >= 0; i--) {
      recentMessages.unshift(aiChatSummary[i]);

      const isWithin1000Limit = isWithinTokenLimit(
        JSON.stringify(recentMessages),
        1000
      );

      if (!isWithin1000Limit) {
        cutoffIndex = i;
        break;
      }
    }

    const aiChatSummaryToSummarize = aiChatSummary.slice(0, cutoffIndex);

    const {
      summary: newSummary,
      tokensBefore,
      tokensAfter,
    } = await summarizeAIChatSummary({
      aiChatSummary: aiChatSummaryToSummarize as string[],
    });

    const newSummaryContent: string[] = [
      ...newSummary,
      ...aiChatSummary.slice(cutoffIndex, aiChatSummary.length + 1),
    ];

    // Log to Firestore
    if (userId && conversationId) {
      addStatToAdmin(
        tokensBefore ?? 0,
        tokensAfter ?? 0,
        userId,
        conversationId,
        "summary",
        aiChatSummaryToSummarize.length,
        aiChatSummaryToSummarize,
        newSummaryContent.length
      ).catch((error) => {
        console.error("Failed to add stats to admin:", error);
      });
    }

    // Log successful summarization to Sentry
    Sentry.addBreadcrumb({
      category: "summarization",
      message: "Successfully summarized AI chat summary",
      level: "info",
      data: {
        userId,
        conversationId,
        chunkId: lastChunk.id,
        tokensBefore,
        tokensAfter,
        summaryItemsCount: aiChatSummaryToSummarize.length,
      },
    });

    // Capture as a message for better tracking
    Sentry.captureMessage("AI Chat Summary Summarized", {
      level: "info",
      tags: {
        userId,
        conversationId,
        chunkId: lastChunk.id,
        component: "checkAndSummarizeAIChatSummary",
        tokensBefore: tokensBefore ?? 0,
        tokensAfter: tokensAfter ?? 0,
        compressionRatio:
          (tokensBefore ?? 0) > 0 && (tokensAfter ?? 0) > 0
            ? ((tokensBefore ?? 0) / (tokensAfter ?? 0)).toFixed(2)
            : "N/A",
      },
    });

    return newSummaryContent;
  } catch (error) {
    console.error("Failed to summarize AI chat summary:", error);
    Sentry.captureException(error, {
      tags: {
        userId,
        conversationId,
        chunkId: lastChunk.id,
        component: "checkAndSummarizeAIChatSummary",
        operation: "summarize-summary",
      },
    });
    return [];
  }
};

const addStatToAdmin = async (
  tokensBefore: number,
  tokensAfter: number,
  userId: string,
  conversationId: string,
  type: "messages" | "summary",
  itemCount: number,
  aiChatSummary?: string[],
  newSummaryContentLength?: number
) => {
  try {
    const adminStatsRef = doc(db, "admin/chatSummaryStats");

    // Create the stats object
    const statObject = {
      userId,
      timestamp: new Date(),
      conversationId,
      tokensBefore,
      tokensAfter,
      compressionRatio:
        tokensAfter > 0 ? (tokensBefore / tokensAfter).toFixed(2) : "N/A",
      type,
      itemCount,
      ...(aiChatSummary ? { originalSummary: aiChatSummary } : {}),
      ...(newSummaryContentLength ? { newSummaryContentLength } : {}),
    };

    // Update the document by pushing the new stat to the log2 array
    await updateDoc(adminStatsRef, {
      log2: arrayUnion(statObject),
    });
  } catch (error) {
    console.error("Failed to save summary stats:", error);
    Sentry.captureException(error, {
      tags: {
        userId,
        conversationId,
        component: "summaries",
        operation: "addStatToAdmin",
      },
      extra: {
        tokensBefore,
        tokensAfter,
        type,
      },
    });
  }
};

// const addStatToAdmin = async (tokensBefore: number, tokensAfter: number) => {
//   try {
//     const adminStatsRef = doc(db, "admin/chatSummaryStats");

//     // Create the stats object
//     const statObject = {
//       userId: userData?.uid,
//       timestamp: new Date(),
//       conversationId,
//       tokensBefore,
//       tokensAfter,
//     };

//     // Update the document by pushing the new stat to the log array
//     await updateDoc(adminStatsRef, {
//       log: arrayUnion(statObject),
//     });
//   } catch (error) {
//     console.error("Failed to save summary stats:", error);
//     Sentry.captureException(error, {
//       tags: {
//         userId: userData?.uid,
//         conversationId,
//         component: "useFetchConversationChunks",
//         operation: "addStatToAdmin",
//       },
//       extra: {
//         tokensBefore,
//         tokensAfter,
//       },
//     });
//   }
// };

// const addErrorToAdmin = async (error: any) => {
//   try {
//     const adminErrorsRef = doc(db, "admin/chatSummaryErrors");

//     await updateDoc(adminErrorsRef, {
//       log: arrayUnion({
//         userId: userData?.uid,
//         timestamp: new Date(),
//         conversationId,
//         error,
//       }),
//     });
//   } catch (error) {
//     console.error("Failed to save error:", error);
//     Sentry.captureException(error, {
//       tags: {
//         userId: userData?.uid,
//         conversationId,
//         component: "useFetchConversationChunks",
//         operation: "addErrorToAdmin",
//       },
//     });
//   }
// };
